import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import {
  BENCHMARK_SINGLEREQUESTEDCHANGE,
  BENCHMARK_SCHEDULINGPRODUCTS,
  DATE_FORMAT_UPDATED,
} from "../../constants/app.constants";
import {
  updateSingleRequestedChangeScheduled,
  fetchRequestedChangeScheduled,
  fetchBenchmarkProducts,
} from "./scheduling.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Checkbox from "../../widgets/checkbox";
import DropdownList from "../../widgets/DropdownList";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import TextArea from "../../widgets/textArea";
import { dateFormat } from "../../constants/app.constants";

// ← New MUI imports
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

type OverviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  requestChangeId: number;
};

type UpdatedValue = string[] | string | Date | undefined;

const SchedulingRequestChangeModal: FC<OverviewModalProps> = ({
  requestChangeId,
  isOpen,
  onClose,
  onSubmit
}) => {
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState<Date>();
  const [sensitiveClient, setSensitiveClient] = useState(0);
  const [requestedYardage, setRequestedYardage] = useState("");
  const [requestedProduct, setRequestedProduct] = useState("");
  const [note, setNote] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  const { isFetching, data: dataBenchmarkSingleRequestedChange } = useQuery({
    queryKey: [BENCHMARK_SINGLEREQUESTEDCHANGE, requestChangeId],
    queryFn: () => fetchRequestedChangeScheduled(requestChangeId),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingBenchmarkProducts,
    error: errorBenchmarkProducts,
    data: dataBenchmarkProducts,
    isFetching: isFetchingBenchmarkProducts,
  } = useQuery({
    queryKey: [BENCHMARK_SCHEDULINGPRODUCTS],
    queryFn: () => fetchBenchmarkProducts(),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const onChangeFieldValues = useCallback(
    (id: string, updatedValue: UpdatedValue) => {
      if (id === "requestedDeliveryDate") {
        setRequestedDeliveryDate(updatedValue as Date);
      } else if (id === "requestedYardage") {
        setRequestedYardage(updatedValue as string);
      } else if (id === "requestedProduct") {
        setRequestedProduct(updatedValue as string);
      } else if (id === "note") {
        setNote(updatedValue as string);
      }
    },
    []
  );

  useEffect(() => {
    if (requestChangeId && !isFetching && isOpen) {
      setRequestedDeliveryDate(
        dataBenchmarkSingleRequestedChange?.RequestedDeliveryDate
      );
      setRequestedYardage(dataBenchmarkSingleRequestedChange?.RequestedYardage);
      setRequestedProduct(dataBenchmarkSingleRequestedChange?.RequestedProduct);
      setNote(dataBenchmarkSingleRequestedChange?.Note);
      setSensitiveClient(dataBenchmarkSingleRequestedChange?.SensitiveClient);
      setIsShowLoader(false);
    }
  }, [dataBenchmarkSingleRequestedChange, requestChangeId, isFetching, isOpen]);

  const handleCheckbox = (id: string, isChecked: boolean) => {
    if (id === "sensitiveClient") {
      setSensitiveClient(isChecked ? 1 : 0);
    }
  };

  const handleSubmit = useCallback(() => {
    if (!requestedDeliveryDate) {
      setValidationMsg("Please select requested delivery date.");
    } else if (!requestedYardage) {
      setValidationMsg("Please enter requested yardage.");
    } else if (!requestedProduct) {
      setValidationMsg("Please select requested product.");
    } else {
      setIsShowLoader(true);
      updateSingleRequestedChangeScheduled(
        requestChangeId,
        requestedYardage,
        requestedProduct,
        note,
        requestedDeliveryDate,
        sensitiveClient
      ).then((updateResult) => {
        setIsShowLoader(false);
        setValidationMsg("");
        if (updateResult) {
          Swal.fire({
            customClass: { popup: "sweetAlert" },
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
            title: "Request Sent Successfully!",
          });
          // onClose();
          onSubmit();
        } else {
          Swal.fire({
            icon: "warning",
            title: "Failed to Send",
          });
        }
      });
    }
  }, [
    requestChangeId,
    requestedDeliveryDate,
    requestedYardage,
    requestedProduct,
    note,
    sensitiveClient,
    // onClose,
    onSubmit
  ]);

  const handleCancel = useCallback(() => {
    setRequestedDeliveryDate(undefined);
    setSensitiveClient(0);
    setRequestedYardage("");
    setRequestedProduct("");
    setNote("");
    setIsShowLoader(false);
    setValidationMsg("");
    onClose();
  }, [onClose]);

  return (
    <Modal show={isOpen} label="Request Change" onClose={handleCancel}>
      {validationMsg && <div className="text-danger mb-2">{validationMsg}</div>}
      <div className="requestModalSection">
        <div className="modalForm">
          {(isShowLoader || isFetching) && <Loader />}
          <form>
            <div className="modal-body">
              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Sensitive Client:
                </label>
                <div className="col-md-8 mt-2">
                  <Checkbox
                    id="sensitiveClient"
                    isChecked={!!sensitiveClient}
                    onChange={handleCheckbox}
                  />
                </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Requested Delivery Date:
                  <span className="text-danger">*</span>
                </label>
                <div className="col-md-8">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Select date"
                    value={requestedDeliveryDate ? dayjs(requestedDeliveryDate) : null}
                    onChange={(newVal) => setRequestedDeliveryDate(newVal?.toDate())}
                    format={DATE_FORMAT_UPDATED}
                    disableFuture
                    slotProps={{
                      textField: {
                        size: "small",
                        error: false,          
                        helperText: "",        
                      },
                    }}
                  />
              </LocalizationProvider>
              </div>
            </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Requested Yardage:
                  <span className="text-danger">*</span>
                </label>
                <div className="col-md-2">
                  <TextField
                    id="requestedYardage"
                    onChange={onChangeFieldValues}
                    type="text"
                    defaultValue={requestedYardage}
                  />
                </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Requested Product:
                  <span className="text-danger">*</span>
                </label>
                <div className="col-md-8">
                  <DropdownList
                    id="requestedProduct"
                    dropDownData={dataBenchmarkProducts}
                    onChange={onChangeFieldValues}
                    defaultOption="Select Product"
                    selectedID={requestedProduct}
                    className="form-select"
                  />
                </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">Note:</label>
                <div className="col-md-8">
                  <TextArea
                    id="note"
                    className="form-control textareaInput"
                    onChange={onChangeFieldValues}
                    value={note}
                    rows={5}
                  />
                </div>
              </div>

              <div className="form-group row">
                <div className="col-md-4" />
                <div className="col-md-8 actionButtons">
                  <button
                    type="button"
                    className="btn cancelPwdBtn mb-3"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn submitBtn mb-3"
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulingRequestChangeModal;
